/**
 * Member — the org-side member mirror, one doc per member OR per pending
 * invite, discriminated on `status`.
 *
 * Path: `workspaces/{orgId}/members/{uid}` for an active row (doc id = the
 * member's uid), `workspaces/{orgId}/members/{token}` for a pending row (doc
 * id = the invite token). A DENORMALIZED MIRROR: written only by triggers —
 * the grant → member and user → member triggers for active rows, the
 * invite `created`/`written` triggers for pending rows. No client write
 * path exists — create/update/delete are omitted, so default-deny blocks
 * them.
 *
 * Why it exists (the collection-group problem): a member list built on a
 * collection-group query over `grants` is not authorizable — such a list
 * rule is keyed on data the engine leaves unbound during list evaluation
 * and errors to deny. A path-var-bound list on `workspaces/{orgId}/members`
 * resolves `orgId` from the request path, which is authorizable.
 *
 * Why identity is denormalized here: `users/{uid}` is self-only, so an
 * admin cannot read a colleague's document. Carrying displayName and email
 * means rendering the member list needs no cross-user read.
 *
 * One doc per member, not per scope: events are not tenants, so a member
 * scoped to three events is still one person in one member list — their
 * reach is the `scope` field, not three rows.
 *
 * Fields justified (active | pending, discriminated on `status`):
 *   status       — 'active' once a grant exists, 'pending' while an invite
 *                  is unredeemed. Scripted provisioning alone made every
 *                  row active by construction, expiring the "a discriminant
 *                  with one value distinguishes nothing" rationale — invites
 *                  reintroduce the unredeemed state, so the discriminant
 *                  returns with real work to do.
 *   uid | token  — doc id, per variant — see Path above.
 *   roleId       — FK → workspaces/{orgId}/roles/{roleId}; what they may do,
 *                  or would do once redeemed.
 *   scope        — all-events, or the named events the grants (active) or
 *                  the invite (pending) cover; the member list's "over
 *                  which events" column. A Member-local union, not
 *                  GrantScopeSchema: the provisioning input's all-events
 *                  variant carries an orgId this document must not restate,
 *                  since the parent path segment already binds it.
 *   displayName  — member-list render without a cross-user read. NULLISH on
 *                  active only: the grant is written by provisioning before
 *                  the member has ever signed in, so no users/{uid}
 *                  document exists to read it from yet — a second trigger
 *                  backfills it at first sign-in, and until then the row
 *                  shows the email. Required on pending — the inviter
 *                  supplies it.
 *   email        — member-list render without a cross-user read; personal
 *                  data, which is why the read gate is a held permission.
 *                  Known at provisioning/invite time on both variants, so
 *                  not nullish.
 *   grantedAt    — active only — the member list's timeline column, and the
 *                  provenance line's date. Preserved from the winning grant's
 *                  own `grantedAt` when it carries a real one; falls back to
 *                  this row's prior synthesized value (or a fresh stamp on
 *                  first convergence) when the grant predates that field —
 *                  see grant.entity.ts and reconcileMember.
 *   grantedBy    — active only, NULLISH — uid of the actor who wrote the
 *                  member's winning grant, denormalized straight off it (see
 *                  grant.entity.ts: the grant doc is self-only readable, so
 *                  this mirror is the only copy an admin viewing a
 *                  colleague's detail panel can read). The provenance line
 *                  ("Granted by {name} on {date}") resolves this uid to a
 *                  display name via the already-loaded member list.
 *                  Nullish because a grant written before this field existed
 *                  carries none, and the mirror must still converge without
 *                  crashing on that older shape.
 *   invitedBy    — pending only — the inviting member's uid.
 *   createdAt    — pending only — mirrors
 *                  workspaces/{orgId}/invites/{token}.createdAt.
 *   expiresAt    — pending only — mirrors
 *                  workspaces/{orgId}/invites/{token}.expiresAt; the row is
 *                  deleted (not left stale) on revoke/expire.
 *
 * Excluded:
 *   permissions  — the role doc is the single owner; mirroring would drift.
 *   orgId        — the parent path segment already carries it, which is why
 *                  `scope` is the local union rather than GrantScopeSchema.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import { type Actor, Id } from "./workspace.entity.ts";

export const MemberScopeSchema = z.discriminatedUnion("kind", [
	z.object({ kind: z.literal("all-events") }),
	z.object({ kind: z.literal("events"), eventIds: z.array(Id).min(1) }),
]);
export type MemberScope = z.infer<typeof MemberScopeSchema>;

export const MemberSchema = z.discriminatedUnion("status", [
	z.object({
		status: z.literal("active"),
		uid: Id,
		roleId: Id,
		scope: MemberScopeSchema,
		displayName: z.string().min(1).nullish(),
		email: z.string().email(),
		grantedAt: z
			.custom<Timestamp>()
			.describe("when provisioning wrote the member’s first grant"),
		grantedBy: Id.nullish().describe(
			"uid of the actor who wrote the member's winning grant — nullish when that grant predates this field",
		),
	}),
	z.object({
		status: z.literal("pending"),
		token: Id.describe(
			"doc id — the invite token; the join back to workspaces/{orgId}/invites/{token}",
		),
		roleId: Id,
		scope: MemberScopeSchema,
		displayName: z.string().min(1),
		email: z.string().email(),
		invitedBy: Id,
		createdAt: z.custom<Timestamp>(),
		expiresAt: z.custom<Timestamp>(),
	}),
]);
export type Member = z.infer<typeof MemberSchema>;

// read + list: a held permission, WIDENED to an OR — members.view OR
// members.invite — not bare membership: the member list carries colleague
// names and email addresses, and invite authority implies member-list
// visibility, so a custom role granted invite-without-view cannot silently
// break the member list. `hasPermission` resolves through grant and role
// doc `get()`s (≤2 accesses per arm) and never touches `resource.data`, and
// `{orgId}` is a bound path variable on a subcollection list, so the rule
// stays list-safe and the emitter produces `allow read` AND `allow list` —
// covering both the active and pending rows the same way, since neither
// rule reads `data` at all.
// No write actions declared: triggers own this collection, both variants.
export const Member = entity<Actor>()({
	schema: MemberSchema,
	actions: ["read", "list"] as const,
	namespace: "tickets.members",
})
	.pipe(({ can, hasPermission, or, and }) => [
		can("read").when((_data, me) =>
			and(
				or(
					hasPermission(me.permissions, "tickets.members.view"),
					hasPermission(me.permissions, "tickets.members.invite"),
				),
				me.mfa.eq(true),
			),
		),
		can("list").when((_data, me) =>
			and(
				or(
					hasPermission(me.permissions, "tickets.members.view"),
					hasPermission(me.permissions, "tickets.members.invite"),
				),
				me.mfa.eq(true),
			),
		),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/members/{uid}",
		}),
	);
